import React, { useState } from "react";
import { XMarkIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import toast from "../../../utils/notify";
import axios from "@/services/axios";

const BulkUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const downloadTemplate = () => {
    // Sample template based on LMS_Master_Final.json structure
    const template = [
      {
        title: "Sample Training Module",
        description: "Brief description of the training module",
        duration: "45 min",
        category: "Awareness",
        icon: "Book",
        status: "available",
        progress: 0,
        isoAlignment: ["ISO 27001", "NIST CSF"],
        quizQuestions: 3,
        sections: [
          {
            id: "section-1",
            title: "Introduction",
            targetDuration: 5,
            slides: [
              {
                slideNumber: 1,
                title: "Welcome",
                bullets: ["Point 1", "Point 2"],
                voiceOverScript: "Welcome to the training...",
                duration: 60
              }
            ]
          }
        ],
        quizzes: [
          {
            id: 1,
            question: "Sample Question?",
            options: ["Option A", "Option B", "Option C"],
            correctAnswer: 1
          },
          {
            id: 2,
            question: "Another Sample Question?",
            options: ["Option A", "Option B"],
            correctAnswer: 0
          },
          {
            id: 3,
            question: "Final Sample Question?",
            options: ["Yes", "No", "Maybe"],
            correctAnswer: 2
          }
        ]
      }
    ];

    const element = document.createElement("a");
    const fileBlob = new Blob([JSON.stringify(template, null, 2)], {
      type: "application/json"
    });

    element.href = URL.createObjectURL(fileBlob);
    element.download = "lms_bulk_upload_template.json";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success("Downloaded LMS bulk upload template");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setFile(null);
      setError("");
      return;
    }

    if (!selectedFile.name.endsWith(".json")) {
      setError("Only JSON files are allowed");
      setFile(null);
      return;
    }

    // Optional: Validate JSON format
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        JSON.parse(event.target.result);
        setError("");
        setFile(selectedFile);
      } catch (e) {
        setError("Invalid JSON format");
        setFile(null);
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select a JSON file");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("json_file", file);

    try {
      const response = await axios.post(
        "/api/lms/modules/bulk-upload-combined/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.data.success || response.data.message) {
        const { data } = response.data;
        const moduleCount = data?.modules_created?.length || 0;
        const sectionCount = data?.total_sections || 0;
        const quizCount = data?.total_quizzes || 0;
        
        console.log("Upload Response:", {
          modules: moduleCount,
          sections: sectionCount,
          quizzes: quizCount,
          details: data
        });

        const successMessage = `✓ Uploaded ${moduleCount} module(s), ${sectionCount} section(s), ${quizCount} quiz(zes)`;
        toast.success(successMessage);
        setFile(null);
        setError("");
        onUploadSuccess();
        onClose();
      }
    } catch (err) {
      console.error("Upload error:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to upload LMS data";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl dark:shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-brand to-brand-dark px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-white font-bold text-lg">Bulk Upload LMS Modules</h3>
            <p className="text-blue-100 text-sm mt-1">Upload multiple training modules from a JSON file</p>
          </div>
          <button 
            onClick={onClose} 
            className="text-white/80 hover:text-white transition-colors duration-200"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Form body ── */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          
          {/* Info Box */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <ul className="text-xs text-zinc-100 dark:text-slate-50 space-y-1 list-disc list-inside">
              <li>JSON file with module data structure</li>
              <li>Each module should include: title, description, duration, category, status, quizQuestions</li>
              <li>Include sections array with slide information</li>
              <li>Include quizzes array with questions and correct answers</li>
            </ul>
          </div>

          {/* File Upload */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                JSON File <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={downloadTemplate}
                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download Template
              </button>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
                id="json_file_input"
                disabled={loading}
              />
              <label
                htmlFor="json_file_input"
                className={`relative border-2 border-dashed rounded-xl px-4 py-8 transition-all duration-300 cursor-pointer group flex items-center justify-center ${
                  file
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="text-center">
                  {file ? (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                          <ArrowUpTrayIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-green-700 dark:text-green-400 font-semibold text-xs">{file.name}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full group-hover:scale-110 transition-transform duration-200">
                          <ArrowUpTrayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-semibold text-xs">Drag and drop your file</p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">or click to browse</p>
                      </div>
                    </div>
                  )}
                </div>
              </label>
              {error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="p-4 bg-slate-50 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-2">Instructions:</h3>
            <ol className="text-xs text-slate-700 dark:text-gray-400 list-decimal list-inside space-y-1">
              <li>Download the template JSON file using the button above</li>
              <li>Fill in the template with your module, section, and quiz data</li>
              <li>Ensure the JSON structure matches the provided format</li>
              <li>Upload the completed JSON file</li>
              <li>The system will process and create all modules, sections, and quizzes</li>
            </ol>
          </div>
        </form>

        {/* ── Footer ── */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !file}
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 inline-flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {loading ? "Uploading..." : "Upload LMS"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
